<template>
  <div
    class="form-file-manager"
    :class="[{ preview: preview }, ...wrapperClasses]"
  >
    <div class="title flex mb-2">
      {{ title }}<span v-if="mandatory" class="required ml-2">*</span>
      <div
        v-if="!preview"
        @click="onClickHelp"
        class="flex cursor-pointer ml-2"
      >
        <feather-icon
          :icon="'HelpCircleIcon'"
          class="inline-flex rounded-full"
          svgClasses="h-5"
        ></feather-icon>
      </div>
    </div>
    <div @click.stop="() => {}">
      <vs-collapse>
        <vx-collapse-item icon-pack="feather" icon-arrow="icon-chevron-down">
          <div slot="header">
            <div
              class="flex items-center justify-between cursor-default p-2"
              @click.stop="() => {}"
              v-if="!preview"
            >
              <div class="flex items-center ml-2">
                <common-updated-label
                  :updatedAt="updateDateLabel"
                  :createdAt="createDateLabel"
                />
              </div>
              <div class="flex items-center mr-6">
                <div class="mandatory-switch flex" v-if="showMandatorySwitch">
                  <vs-switch
                    color="#2D9CDB"
                    class="ml-6"
                    v-model="mandatoryValue"
                  />
                  <span class="ml-2">必須</span>
                </div>

                <template v-if="showStatus">
                  <status-chip
                    v-if="isStatusFixed"
                    class="ml-3 mr-2"
                    :label="status.label"
                    :color="status.color"
                  ></status-chip>
                  <status-dropdown-select
                    v-else
                    class="ml-3 mr-2"
                    :options="filteredStatusOptions"
                    :value.sync="status"
                    :onClickOption="onStatusChange"
                    clickEvent
                  ></status-dropdown-select>
                </template>
              </div>
            </div>
          </div>
          <vs-divider class="m-0"></vs-divider>
          <div class="upload-area p-2">
            <div class="flex items-center">
              <div class="w-full mr-4">
                <common-input-file ref="FileInput"></common-input-file>
              </div>
              <div class="w-32 mb-4 mr-2">
                <vs-button
                  class="w-full"
                  size="small"
                  :color="AISEA_COLOR.BLUE"
                  @click="onClickSaveButton()"
                  >保存</vs-button
                >
              </div>
            </div>
          </div>
          <div v-for="(item, index) in files" :key="index">
            <common-file-list-item
              :file="item"
              :checked="item.checked"
              :ref="`ListItem${index}`"
              :deleteFile="payload => onClickDelete(payload.name)"
              :downloadFile="
                payload => onClickDownloadFile(payload.name, payload.token)
              "
              :onClickFileLink="
                payload => onClickFileLink(payload.name, payload.token)
              "
              :id="inputValue.id"
            ></common-file-list-item>
          </div>
        </vx-collapse-item>
      </vs-collapse>
    </div>
  </div>
</template>

<script>
import CommonFileListItem from "../molecules/CommonFileListItem.vue";
import CommonInputFile from "../molecules/CommonInputFile.vue";
import CommonUpdatedLabel from "../molecules/CommonUpdatedLabel.vue";
import VxCollapseItem from "./../../../../../../components/VxCollapseItem.vue";
import { ColorMixin, HullInsranceMixin } from "@/mixins";
import { toYMDHm_jp } from "@/utils/entity/DateUtil";
import StatusDropdownSelect from "../organisms/StatusDropdownSelect.vue";
import StatusChip from "../molecules/StatusChip.vue";

export default {
  components: {
    CommonFileListItem,
    CommonInputFile,
    CommonUpdatedLabel,
    VxCollapseItem,
    StatusDropdownSelect,
    StatusChip,
  },
  name: "O.FormFileManager",
  mixins: [ColorMixin, HullInsranceMixin],
  props: {
    wrapperClasses: {
      type: Array, // Array<String>
      default() {
        return [];
      },
    },
    preview: {
      type: Boolean,
      default: false,
    },
    title: { type: String, required: true },
    inputValue: {
      type: Array,
      default() {
        return [];
      },
    },
    onClickDelete: { type: Function, default: () => {} },
    onClickDownloadFile: { type: Function, default: () => {} },
    onClickFileLink: { type: Function, default: () => {} },
    onClickSave: {
      type: Function,
      default: () => {},
    },
    onSwitchChange: {
      type: Function,
      default: () => {},
    },
    onClickHelp: {
      type: Function,
      default: () => {},
    },
    onStatusChange: {
      type: Function,
      default: () => {},
    },
    createDate: {
      type: Number,
    },
    updateDate: {
      type: Number,
    },
    showRequiredSwitch: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialSwitchValue: {
      type: Boolean,
      default: false,
    },
    initialStatusValue: {
      type: Object,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    mandatory: {
      type: Boolean,
    },
    isAdmin: {
      type: Boolean,
    },
  },
  data() {
    return {
      files: [],
      mandatoryValue: this.initialSwitchValue,
      status: null,
    };
  },
  watch: {
    mandatoryValue(value) {
      this.onSwitchChange(value);
    },
    inputValue: {
      handler: function() {
        this.setFiles();
      },
      deep: true,
    },
  },
  computed: {
    createDateLabel() {
      return toYMDHm_jp(this.createDate);
    },
    updateDateLabel() {
      return toYMDHm_jp(this.updateDate);
    },
    showMandatorySwitch() {
      if (this.isAdmin) {
        return true;
      } else {
        return false;
      }
    },
    filteredStatusOptions() {
      return this.statusOptions.filter(element => element.value != 1);
    },
    isStatusFixed() {
      return this.status.value == 1 || !this.isAdmin;
    },
    showStatus() {
      return !this.preview && this.mandatory;
    },
  },
  methods: {
    setFiles() {
      if (!this.inputValue) {
        this.files = [];
        return;
      }
      this.files = this.inputValue.map(item => {
        return item;
      });
    },
    onClickSaveButton() {
      if (this.$refs.FileInput.files.length == 0) return;

      this.onClickSave(this.$refs.FileInput.files);
      this.$refs.FileInput.initFiles([]);
    },
  },
  created() {
    this.setFiles();
    this.status = this.initialStatusValue;
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "@/assets/scss/aisea/hull-insurance.scss";

.form-file-manager {
  .required {
    font-weight: bold;
    color: $aisea-red;
  }
  .mandatory-switch {
    align-items: center;
  }
  &.preview {
    pointer-events: none;
  }
  .vs-collapse {
    padding: 0;

    header {
      margin: 0;
      padding: 0;
    }

    .vs-collapse-item--header {
      min-height: 39px;
      padding: 0;
      margin: 0;
      padding: 0px;
      font-size: 12px;
      font-family: $aisea-font-family;

      .vs-collapse-item--icon-header {
        display: flex;
        align-items: center;
        .vs-icon {
          width: 18px;
          color: $item-mono2;
        }
      }

      .list-expand {
        border-radius: 6px 6px 0 0;
        background: #f333;
      }
    }

    .vs-collapse-item {
      border: solid 1px $border-color1;
      border-bottom: solid 1px$border-color1 !important;
      border-radius: 6px;
      background-color: $aisea-pure-white;
    }
  }

  .con-content--item {
    margin: 0px !important;
    padding: 0px !important;
  }

  .icon-button {
    color: $item-mono2;
  }

  .unregistered-label {
    background-color: $aisea-red;
    font-weight: bold;
    color: $aisea-pure-white;
    border-radius: 4px;
    padding: 2px 8px;
    margin: 0 2px;
  }
}
</style>
